import { HVButton, HVRadio, HVScrollBar, HVText } from "@repo/HVComponents";
import { generateTimezoneList } from "@utils/Timezone";
import { useState, ChangeEvent, useMemo, useEffect, useRef } from "react";
import { useLang } from "@hooks/useLang";
import { getRadioCommonStyles } from "@components/common/RadioCommonStyles";
import { SESSION_STORAGE_KEY } from "@resources/Constants";
import * as S from "./TimeZonestyled";

interface TimeZoneProps {
  mode: string;
  setMode: (mode: string) => void;
  setUtc: (utc: string) => void;
  setCurrentTimeZone?: (currentTimeZone: string) => void;
  timeZone?: string;
  currentUserTimeZone?: string;
  storeSession?: boolean;
}

export const TimeZone = (props: TimeZoneProps) => {
  const { translate } = useLang();
  const {
    setUtc,
    mode,
    setMode,
    setCurrentTimeZone,
    timeZone,
    currentUserTimeZone,
    storeSession
  } = props;
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [changeTimeZone, setChangeTimeZone] = useState(timeZone);
  const [initialScrolled, setInitialScrolled] = useState(false);
  const onChangeUtc = (timeZone: string) => {
    const utcItem = generateTimezoneList().filter((item) => {
      return item?.value === timeZone;
    });
    if (utcItem.length > 0) {
      const label = utcItem[0].label.split(" ");
      setUtc(label[0].replace(/[()]/g, "") as string);
    }
  };

  const userTimeZone = useMemo(() => {
    const utcItem = generateTimezoneList().filter((item) => {
      return item?.value === currentUserTimeZone;
    });
    if (utcItem.length > 0) {
      const label = utcItem[0].label.split(" ");
      return label[0].replace(/[()]/g, "") as string;
    }
    return "";
  }, [currentUserTimeZone]);

  const handleTimeSetup = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setMode(value);
    if (value === "os") {
      onChangeUtc(currentUserTimeZone as string);
      setChangeTimeZone(currentUserTimeZone);
      setCurrentTimeZone?.(currentUserTimeZone as string);
    }
  };

  const handleTimeZone = (value: string) => {
    setChangeTimeZone(value);
    onChangeUtc(value);
    setCurrentTimeZone?.(value);
  };

  useEffect(() => {
    if (initialScrolled) return;
    const node = itemRefs.current[changeTimeZone as string];
    if (node && timeZone !== currentUserTimeZone) {
      node.scrollIntoView({
        behavior: "smooth",
        block: "nearest"
      });
      setInitialScrolled(true);
    }
  }, [changeTimeZone]);

  useEffect(() => {
    if (timeZone && storeSession) {
      sessionStorage.setItem(
        SESSION_STORAGE_KEY.TIME_ZONE,
        JSON.stringify(timeZone)
      );
      sessionStorage.setItem(
        SESSION_STORAGE_KEY.DATABASE_TIME_ZONE,
        JSON.stringify(timeZone)
      );
    }
  }, [timeZone]);

  return (
    <S.TimeZoneContainer>
      <S.TimeZoneHeaderContent>
        <HVText type="Body-sm" c="var(--hv-text-normal-tertiary)">
          {translate("time_zone_settings")}
        </HVText>
        <S.UserTimeZone>
          <HVRadio
            label={translate("user_time_zone")}
            value="os"
            checked={mode === "os" && timeZone === currentUserTimeZone}
            onChange={handleTimeSetup}
            size="xs"
            styles={{
              ...getRadioCommonStyles(mode === "os"),
              root: {
                height: "32px",
                flexDirection: "column",
                justifyContent: "center",
                display: "flex"
              }
            }}
          />
          <HVText type="Body-sm" c="var(--hv-text-normal-tertiary)">
            {userTimeZone}
          </HVText>
        </S.UserTimeZone>
        <HVRadio
          label={translate("time_zone_manual")}
          value="manual"
          checked={mode === "manual" || timeZone !== currentUserTimeZone}
          onChange={handleTimeSetup}
          size="xs"
          styles={{
            ...getRadioCommonStyles(mode === "manual"),
            root: {
              height: "32px",
              flexDirection: "column",
              justifyContent: "center",
              display: "flex"
            }
          }}
        />
      </S.TimeZoneHeaderContent>
      <S.Divider />
      <S.TimeZoneMiddleContent>
        <HVText
          type="Body-sm"
          c="var(--hv-text-normal-tertiary)"
          style={{
            alignItems: "left",
            paddingLeft: "20px",
            paddingTop: "12px",
            paddingBottom: "4px"
          }}
        >
          {translate("time_zone")}
        </HVText>
        <S.TimezoneList>
          <HVScrollBar
            scrollBarProps={{
              h: "calc(100% - 85px)",
              pt: 8,
              pb: 0,
              pr: 0,
              pl: 0
            }}
          >
            {generateTimezoneList().map((data) => {
              return (
                <S.TimeZoneContent
                  key={data.value}
                  hasActive={changeTimeZone === data.value}
                  ref={(el) => {
                    itemRefs.current[data.value] = el;
                    if (
                      el &&
                      changeTimeZone === data.value &&
                      timeZone !== currentUserTimeZone &&
                      !initialScrolled
                    ) {
                      el.scrollIntoView({
                        block: "nearest",
                        inline: "nearest"
                      });
                    }
                  }}
                >
                  <HVButton
                    variant="Ghost"
                    style={{
                      padding: 0,
                      background: "transparent",
                      display: "flex",
                      width: "100%"
                    }}
                    onClick={() => handleTimeZone(data.value)}
                    buttonContainerProps={{
                      disabled:
                        (timeZone === currentUserTimeZone && mode === "os") ||
                        data.value === currentUserTimeZone
                    }}
                  >
                    <HVText
                      type="Body-sm"
                      c={
                        timeZone === currentUserTimeZone && mode === "os"
                          ? "var(--hv-text-normal-disabled)"
                          : "var(--hv-text-normal-primary)"
                      }
                    >
                      {data.label}
                    </HVText>
                  </HVButton>
                </S.TimeZoneContent>
              );
            })}
          </HVScrollBar>
        </S.TimezoneList>
      </S.TimeZoneMiddleContent>
    </S.TimeZoneContainer>
  );
};
